import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, Shield, Sparkles, Building2, Briefcase,
  Compass, Target, Mail, X, ChevronLeft,
  // Icons available for the editable "How It Works" steps
  FileText, Lock, Search, Award, ClipboardCheck, Calendar, Handshake, TrendingUp,
  // Icons used by the "Every Deal Is" trust cards
  Users, ShieldCheck, Gem, BadgeCheck, Layers, Scale,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#D4AF37';

// Currency symbol map (matches admin select options)
const CURRENCY_SYMBOL = { GBP: '£', USD: '$', EUR: '€', AED: 'د.إ', GHS: '₵' };

// Icon map for the "Every Deal Is" cards.  Admins pick a fitting icon per point;
// legacy points without an icon fall back to the rotation below.
const EVERY_DEAL_ICONS = {
  Target, Users, ShieldCheck, Gem, BadgeCheck, Layers, Scale,
  Award, Shield, Sparkles, Check, Compass, Briefcase,
};
const DEFAULT_EVERY_DEAL_ICONS = ['Target', 'Users', 'ShieldCheck'];

// Map icon name → Lucide component for editable process-step icons.
// Keep keys here in sync with the admin Bespoke tab icon dropdown.
const PROCESS_ICONS = {
  FileText, Lock, Search, Award, ClipboardCheck, Calendar, Handshake, TrendingUp, Sparkles, Shield, Compass, Briefcase, Target, Building2,
};

// ----- Reference data -----
const COUNTRIES = [
  'United Kingdom', 'Ghana', 'United States', 'Canada', 'Nigeria', 'British Virgin Islands',
  'Qatar', 'United Arab Emirates', 'Saudi Arabia', 'India', 'Singapore',
  'Hong Kong', 'Australia', 'New Zealand', 'France', 'Germany', 'Spain', 'Portugal',
  'Italy', 'Netherlands', 'Switzerland', 'Ireland', 'Egypt', 'Morocco',
  'Côte d\'Ivoire', 'Senegal', 'Tanzania', 'Uganda', 'Rwanda', 'Zambia', 'Zimbabwe',
];

const ASSET_CLASSES = [
  { id: 'residential', label: 'Residential', icon: '🏘️' },
  { id: 'commercial', label: 'Commercial', icon: '🏢' },
  { id: 'land', label: 'Land', icon: '🌳' },
  { id: 'hospitality', label: 'Hospitality / Hotel', icon: '🏨' },
  { id: 'mixed-use', label: 'Mixed-Use', icon: '🏬' },
  { id: 'development', label: 'Development', icon: '🏗️' },
  { id: 'student', label: 'Student Accommodation', icon: '🎓' },
  { id: 'industrial', label: 'Industrial / Logistics', icon: '🏭' },
];

const STRATEGIES = [
  'Buy-to-Let (BTL)', 'BRRR', 'Airbnb / Serviced Accommodation', 'Flip / Value-Add',
  'Development', 'Land Banking', 'Off-Plan', 'Capital Growth', 'Income Producing Assets',
  'JV / Partnership', 'Other'
];

const BUDGETS = [
  'Under £100k', '£100k – £250k', '£250k – £500k', '£500k – £1m',
  '£1m – £2.5m', '£2.5m – £5m', '£5m – £10m', '£10m+'
];

const TIMELINES = ['Immediate', 'Within 3 months', '3–6 months', '6–12 months', '12+ months'];
const HOLDING_PERIODS = ['1–3 years', '3–5 years', '5–10 years', '10+ years', 'Flexible'];
const FUNDING = ['Cash Buyer', 'Mortgage / Financing', 'JV / Partnership', 'Open to Structuring'];
const EXPERIENCE = ['First-Time Investor', 'Experienced Investor', 'Institutional / Professional', 'Diaspora Buyer'];
const RISK = ['Conservative', 'Balanced', 'Growth', 'Opportunistic'];

const STEPS = [
  { id: 1, label: 'Contact', icon: Mail },
  { id: 2, label: 'Acquisition Criteria', icon: Compass },
  { id: 3, label: 'Investor Profile', icon: Briefcase },
  { id: 4, label: 'Preferences', icon: Sparkles },
];

// Tiny helpers
const FieldLabel = ({ children, required, hint }) => (
  <label className="block">
    <span className="text-white/70 text-xs uppercase tracking-[0.18em] font-semibold">
      {children}{required && <span className="text-[#D4AF37] ml-1">*</span>}
    </span>
    {hint && <span className="block text-white/30 text-[11px] mt-0.5 normal-case tracking-normal">{hint}</span>}
  </label>
);

const TextInput = ({ value, onChange, placeholder, type = 'text', error, ...rest }) => (
  <>
    <input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`mt-2 w-full bg-white/[0.04] border ${error ? 'border-red-400/70 focus:border-red-400' : 'border-white/10 hover:border-white/25 focus:border-[#D4AF37]/60'} rounded-lg px-4 py-3 text-white text-sm focus:outline-none transition-colors placeholder:text-white/25`}
      {...rest}
    />
    {error && <p className="text-red-400 text-[11px] mt-1.5" data-testid={`${rest['data-testid'] || 'input'}-error`}>{error}</p>}
  </>
);

const TextArea = ({ value, onChange, placeholder, rows = 4, ...rest }) => (
  <textarea
    value={value || ''}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className="mt-2 w-full bg-white/[0.04] border border-white/10 hover:border-white/25 focus:border-[#D4AF37]/60 rounded-lg px-4 py-3 text-white text-sm focus:outline-none transition-colors resize-none placeholder:text-white/25"
    {...rest}
  />
);

const SelectInput = ({ value, onChange, options, placeholder = 'Select…', ...rest }) => (
  <select
    value={value || ''}
    onChange={e => onChange(e.target.value)}
    className="mt-2 w-full bg-white/[0.04] border border-white/10 hover:border-white/25 focus:border-[#D4AF37]/60 rounded-lg px-4 py-3 text-white text-sm focus:outline-none transition-colors appearance-none cursor-pointer"
    {...rest}
  >
    <option value="" className="bg-[#0a1a14]">{placeholder}</option>
    {options.map(o => (
      <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value} className="bg-[#0a1a14]">
        {typeof o === 'string' ? o : o.label}
      </option>
    ))}
  </select>
);

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-xs px-3 py-2 rounded-full border transition-all font-medium ${
      active
        ? 'border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]'
        : 'border-white/15 bg-white/[0.02] text-white/70 hover:border-white/30 hover:text-white'
    }`}
  >
    {children}
  </button>
);

const MultiSelectChips = ({ options, value = [], onChange, getLabel = o => o, getValue = o => o, getKey = o => o }) => (
  <div className="flex flex-wrap gap-2 mt-2">
    {options.map(o => {
      const v = getValue(o);
      const active = value.includes(v);
      return (
        <Chip key={getKey(o)} active={active} onClick={() => {
          onChange(active ? value.filter(x => x !== v) : [...value, v]);
        }}>
          {getLabel(o)}
        </Chip>
      );
    })}
  </div>
);

const RadioCards = ({ options, value, onChange, columns = 4 }) => (
  <div className={`grid grid-cols-2 md:grid-cols-${columns} gap-2 mt-2`}>
    {options.map(o => {
      const v = typeof o === 'string' ? o : o.value;
      const label = typeof o === 'string' ? o : o.label;
      const icon = typeof o === 'string' ? null : o.icon;
      const active = value === v;
      return (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={`text-left p-3 rounded-lg border transition-all ${
            active
              ? 'border-[#D4AF37] bg-[#D4AF37]/12'
              : 'border-white/10 bg-white/[0.02] hover:border-white/25'
          }`}
        >
          <div className="flex items-center gap-2">
            {icon && <span className="text-base">{icon}</span>}
            <span className={`text-sm font-medium ${active ? 'text-[#D4AF37]' : 'text-white/80'}`}>{label}</span>
          </div>
        </button>
      );
    })}
  </div>
);

// ============== Main page ==============
const BespokeSourcingPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', company: '', country_residence: '',
    preferred_countries: [], preferred_cities: '',
    asset_classes: [], strategies: [], other_strategy: '',
    investment_budget: '', target_yield: '', capital_growth: '',
    holding_period: '', timeline: '',
    funding_position: [], deposit_available: '',
    experience_level: '', risk_appetite: '',
    unit_preference: '', growth_vs_rental: '',
    amenities: '', requirements_exclusions: '', additional_notes: '',
    consent: false, mailing_list: false,
    // Channel opt-ins for portal updates
    notify_email: true, notify_whatsapp: false, notify_telegram: false,
  });
  const [errors, setErrors] = useState({});
  const f = (k, v) => { setForm(prev => ({ ...prev, [k]: v })); setErrors(prev => ({ ...prev, [k]: '' })); };

  // ----- Page settings (hero copy + "How It Works" steps) loaded from backend -----
  const [pageSettings, setPageSettings] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/public/bespoke-sourcing-settings`);
        if (r.ok && !cancelled) setPageSettings(await r.json());
      } catch { /* fall back to hard-coded defaults below */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  // Returns an errors object — empty means the step is valid.
  const validateStep = () => {
    const e = {};
    if (step === 1) {
      if (!form.full_name.trim()) e.full_name = 'Required';
      if (!form.email.trim()) e.email = 'Required';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Please enter a valid email (e.g. you@email.com)';
    }
    if (step === 4 && !form.consent) e.consent = 'Please confirm the consent statement';
    return e;
  };

  const next = () => {
    const e = validateStep();
    if (Object.keys(e).length) {
      setErrors(e);
      toast.error(e.email || e.full_name || e.consent || 'Please complete the required fields');
      return;
    }
    setErrors({});
    if (step < 4) setStep(step + 1);
    else submit();
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/public/bespoke-sourcing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          notify_prefs: {
            email:    !!form.notify_email,
            whatsapp: !!form.notify_whatsapp,
            telegram: !!form.notify_telegram,
          },
        }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Submission failed');
      }
    } catch (_e) {
      toast.error('Network error. Please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a1a14] text-white flex items-center justify-center p-6">
        <div className="max-w-xl w-full text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#D4AF37]/15 flex items-center justify-center border border-[#D4AF37]/30 mb-6">
            <Check className="w-7 h-7 text-[#D4AF37]" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-3">Brief Received</p>
          <h1 className="text-3xl md:text-4xl font-light tracking-tight mb-4">Thank you, {form.full_name.split(' ')[0]}.</h1>
          <p className="text-white/60 leading-relaxed mb-2">Your bespoke sourcing brief has reached our private desk.</p>
          <p className="text-white/60 leading-relaxed mb-8">A senior team member will personally review your requirements and contact you within <span className="text-[#D4AF37] font-semibold">48 business hours</span>.</p>
          <div className="space-y-2 text-[11px] text-white/40 uppercase tracking-[0.3em] mb-8">
            <p>Confidential · Curated · Investor-Led</p>
          </div>
          <button
            onClick={() => navigate('/deals')}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B8941E] text-[#0a1a14] text-xs font-bold uppercase tracking-[0.18em] hover:shadow-lg hover:shadow-[#D4AF37]/30 transition-all"
            data-testid="bespoke-thanks-back"
          >
            <ChevronLeft className="w-4 h-4" /> Back to Live Deals
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1a14] text-white" data-testid="bespoke-page">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-[#0a1a14]/95 backdrop-blur-md border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/deals" className="flex items-center gap-2 text-white/50 hover:text-white text-[11px] uppercase tracking-[0.18em] font-semibold transition-colors" data-testid="bespoke-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Deals
          </Link>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-semibold flex items-center gap-2">
            <Shield className="w-3.5 h-3.5" /> Confidential
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-6 pt-12 pb-8 text-center">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] mb-4">
          {pageSettings?.hero_eyebrow || 'Bespoke Deal Sourcing'}
        </p>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extralight tracking-tight leading-[1.05] mb-5">
          {pageSettings?.hero_title_pre ?? 'Tell us what '}
          <span className="italic font-light text-[#D4AF37]">
            {pageSettings?.hero_title_italic ?? 'you'}
          </span>
          {pageSettings?.hero_title_post ?? ' are looking for.'}
        </h1>
        <p className="text-white/55 max-w-2xl mx-auto leading-relaxed text-base">
          {pageSettings?.hero_subtitle ||
            "The opportunities you see publicly are a fraction of what passes through our desk. Share your acquisition criteria — we'll surface matches from our private pipeline."}
        </p>
        <p className="mt-6 text-[10px] uppercase tracking-[0.3em] text-white/35">
          {pageSettings?.hero_tagline || 'Confidential · Curated · Investor-Led'}
        </p>
      </div>

      {/* "How Our Bespoke Sourcing Works" — process overview */}
      {pageSettings?.process_steps?.length > 0 && (
        <div className="max-w-6xl mx-auto px-6 pt-2 pb-10" data-testid="bespoke-process-section">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] mb-3">The Process</p>
            <h2 className="text-2xl md:text-3xl font-extralight tracking-tight mb-3">
              {pageSettings.process_title}
            </h2>
            {pageSettings.process_subtitle && (
              <p className="text-white/45 text-sm max-w-xl mx-auto">{pageSettings.process_subtitle}</p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
            {pageSettings.process_steps.map((s, i) => {
              const Icon = PROCESS_ICONS[s.icon] || PROCESS_ICONS.FileText;
              return (
                <div
                  key={i}
                  className="relative bg-[#0a1a14] p-7 group hover:bg-[#0e2218] transition-colors"
                  data-testid={`bespoke-process-step-${i}`}
                >
                  {/* Step number */}
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/60 font-semibold mb-4">
                    Step {String(i + 1).padStart(2, '0')}
                  </p>
                  {/* Icon */}
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4 border border-[#D4AF37]/25 bg-[#D4AF37]/[0.06] group-hover:bg-[#D4AF37]/12 transition-colors">
                    <Icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  {/* Title */}
                  <h3 className="text-white font-semibold text-base mb-2.5 tracking-tight">
                    {s.title}
                  </h3>
                  {/* Description */}
                  <p className="text-white/50 text-[13px] leading-relaxed">
                    {s.description}
                  </p>
                  {/* Gold thin accent line on top — animates on hover */}
                  <span
                    className="absolute top-0 left-0 h-0.5 bg-[#D4AF37] transition-all duration-500"
                    style={{ width: '30%' }}
                  />
                </div>
              );
            })}
          </div>

          {/* CTA scroll-to-form hint */}
          <p className="text-center text-[10px] uppercase tracking-[0.3em] text-white/35 mt-8">
            ↓ Start your brief below
          </p>
        </div>
      )}

      {/* ============= COMMITMENT / REFUNDABLE DEPOSIT SECTION ============= */}
      {pageSettings?.commitment_enabled && (
        <div className="max-w-5xl mx-auto px-6 pb-14" data-testid="bespoke-commitment-section">
          {/* Sleek divider */}
          <div className="flex items-center gap-4 mb-12">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] font-semibold whitespace-nowrap">
              {pageSettings.commitment_eyebrow || 'Our Commitment'}
            </p>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
          </div>

          {/* ──────────── EVERY DEAL IS ──────────── */}
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-extralight tracking-tight mb-2 italic">
              <span className="text-white">{pageSettings.every_deal_title || 'Every Deal Is'}</span>
              <span className="text-[#D4AF37]">…</span>
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {(pageSettings.every_deal_points || []).map((point, i) => {
              // Back-compat: point may be a plain string OR an object {text, icon}
              const isObj = point && typeof point === 'object';
              const text = isObj ? point.text : point;
              const iconName = (isObj && point.icon) || DEFAULT_EVERY_DEAL_ICONS[i % DEFAULT_EVERY_DEAL_ICONS.length];
              const Icn = EVERY_DEAL_ICONS[iconName] || Target;
              return (
                <div
                  key={i}
                  className="group flex items-start gap-3.5 p-5 bg-white/[0.02] border border-white/10 rounded-xl hover:border-[#D4AF37]/30 hover:bg-white/[0.04] transition-all"
                  data-testid={`bespoke-every-deal-${i}`}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#D4AF37]/[0.08] border border-[#D4AF37]/25 flex items-center justify-center group-hover:bg-[#D4AF37]/[0.14] group-hover:scale-105 transition-all">
                    <Icn className="w-4.5 h-4.5 text-[#D4AF37]" strokeWidth={1.75} style={{ width: 18, height: 18 }} />
                  </div>
                  <p className="text-white/85 text-sm leading-relaxed pt-0.5">{text}</p>
                </div>
              );
            })}
          </div>

          {pageSettings.every_deal_footer && (
            <p className="text-center text-white/50 text-sm italic max-w-2xl mx-auto mb-16">
              — {pageSettings.every_deal_footer} —
            </p>
          )}

          {/* ──────────── STRUCTURED PROCESS + DEPOSIT CALLOUT ──────────── */}
          <div className="relative bg-gradient-to-br from-[#D4AF37]/[0.04] via-[#0a1a14] to-[#0a1a14] border border-[#D4AF37]/20 rounded-2xl p-8 md:p-12 overflow-hidden">
            {/* Decorative gold corners */}
            <div className="absolute top-0 left-0 w-20 h-20 pointer-events-none" style={{
              background: `linear-gradient(135deg, ${GOLD}40 0%, transparent 60%)`,
            }} />
            <div className="absolute bottom-0 right-0 w-20 h-20 pointer-events-none" style={{
              background: `linear-gradient(315deg, ${GOLD}40 0%, transparent 60%)`,
            }} />

            <div className="relative text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-light tracking-tight mb-3 text-white">
                {pageSettings.commitment_process_title || 'A Structured, Risk-Controlled Process'}
              </h3>
              <p className="text-white/55 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                {pageSettings.commitment_process_intro}
              </p>
            </div>

            {/* Deposit highlight card */}
            <div className="relative max-w-md mx-auto bg-[#0a1a14]/80 border-2 border-[#D4AF37]/40 rounded-xl p-6 mb-10 text-center shadow-2xl shadow-[#D4AF37]/5">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/70 mb-2 font-semibold">
                To Activate
              </div>
              <div className="flex items-baseline justify-center gap-1 mb-2">
                <span className="text-[#D4AF37] text-5xl md:text-6xl font-light tracking-tight" data-testid="bespoke-deposit-amount">
                  {(CURRENCY_SYMBOL[pageSettings.deposit_currency] || '£')}{Number(pageSettings.deposit_amount || 799).toLocaleString()}
                </span>
              </div>
              <p className="text-white/70 text-sm font-medium">
                {pageSettings.deposit_label || 'Fully refundable holding deposit'}
              </p>
              <p className="text-white/40 text-[11px] mt-2 uppercase tracking-[0.18em]">
                Activates a {pageSettings.deposit_period_weeks || 6}-week dedicated search
              </p>
            </div>

            {/* This allows us to */}
            <div className="relative max-w-2xl mx-auto">
              <p className="text-center text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] mb-5 font-semibold">
                {pageSettings.this_allows_title || 'This Allows Us To'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {(pageSettings.this_allows_points || []).map((p, i) => (
                  <div key={i} className="text-center p-4 bg-white/[0.03] rounded-lg border border-white/5" data-testid={`bespoke-allows-${i}`}>
                    <div className="w-7 h-7 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/25 flex items-center justify-center mx-auto mb-2.5">
                      <Check className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={2.5} />
                    </div>
                    <p className="text-white/75 text-[13px] leading-snug">{p}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Refund guarantee strip */}
            {pageSettings.refund_text && (
              <div className="relative mt-10 pt-6 border-t border-[#D4AF37]/15">
                <div className="flex items-start gap-3 max-w-2xl mx-auto">
                  <Shield className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <p className="text-white/65 text-[13px] md:text-sm leading-relaxed">
                    <span className="text-[#D4AF37] font-semibold">Refund guarantee.</span>{' '}
                    {pageSettings.refund_text}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Progress */}
      <div className="max-w-5xl mx-auto px-6 pb-2">
        <div className="grid grid-cols-4 gap-2">
          {STEPS.map(s => {
            const reached = step >= s.id;
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => step > s.id && setStep(s.id)}
                disabled={step < s.id}
                className={`text-left transition-all ${step > s.id ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <div className={`h-1 rounded-full ${reached ? 'bg-[#D4AF37]' : 'bg-white/10'} transition-colors`} />
                <div className="flex items-center gap-2 mt-2.5">
                  <Icon className={`w-3.5 h-3.5 ${reached ? 'text-[#D4AF37]' : 'text-white/30'}`} />
                  <p className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${reached ? 'text-white' : 'text-white/35'}`}>
                    {s.label}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Form card */}
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-7 md:p-10 space-y-6">

          {step === 1 && (
            <div className="space-y-6" data-testid="bespoke-step-1">
              <SectionHeading icon={Mail} title="Contact Information" subtitle="So our team can reach you discreetly." />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <FieldLabel required>Full Name</FieldLabel>
                  <TextInput value={form.full_name} onChange={v => f('full_name', v)} placeholder="Jane Doe" data-testid="bespoke-full-name" error={errors.full_name} />
                </div>
                <div>
                  <FieldLabel required>Email Address</FieldLabel>
                  <TextInput type="email" value={form.email} onChange={v => f('email', v)} placeholder="jane@company.com" data-testid="bespoke-email" error={errors.email} />
                </div>
                <div>
                  <FieldLabel hint="Used for WhatsApp briefings">Phone / WhatsApp</FieldLabel>
                  <TextInput value={form.phone} onChange={v => f('phone', v)} placeholder="+44 7700 900123" data-testid="bespoke-phone" />
                </div>
                <div>
                  <FieldLabel>Company / Investor Type</FieldLabel>
                  <TextInput value={form.company} onChange={v => f('company', v)} placeholder="Family office, fund, individual…" data-testid="bespoke-company" />
                </div>
                <div className="md:col-span-2">
                  <FieldLabel>Country of Residence</FieldLabel>
                  <SelectInput value={form.country_residence} onChange={v => f('country_residence', v)} options={COUNTRIES} data-testid="bespoke-country-res" />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="bespoke-step-2">
              <SectionHeading icon={Compass} title="Acquisition Criteria" subtitle="Help us narrow the universe." />

              <div>
                <FieldLabel hint="Pick all that apply">Preferred Countries</FieldLabel>
                <MultiSelectChips
                  options={['United Kingdom', 'Ghana', 'United States', 'United Arab Emirates', 'Portugal', 'Spain', 'British Virgin Islands', 'Nigeria', 'Qatar', 'Australia', 'Other'].map(c => c)}
                  value={form.preferred_countries}
                  onChange={v => f('preferred_countries', v)}
                />
              </div>

              <div>
                <FieldLabel>Preferred Cities / Locations</FieldLabel>
                <TextInput value={form.preferred_cities} onChange={v => f('preferred_cities', v)} placeholder="e.g. London (W1, W2), Manchester, Accra Airport City" data-testid="bespoke-cities" />
              </div>

              <div>
                <FieldLabel hint="Pick all that apply">Asset Class</FieldLabel>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  {ASSET_CLASSES.map(a => {
                    const active = form.asset_classes.includes(a.id);
                    return (
                      <button key={a.id} type="button" onClick={() => f('asset_classes', active ? form.asset_classes.filter(x => x !== a.id) : [...form.asset_classes, a.id])}
                        className={`p-3 rounded-lg border text-left transition-all ${active ? 'border-[#D4AF37] bg-[#D4AF37]/12' : 'border-white/10 bg-white/[0.02] hover:border-white/25'}`}
                        data-testid={`bespoke-asset-${a.id}`}>
                        <div className="text-base mb-1">{a.icon}</div>
                        <p className={`text-xs font-medium ${active ? 'text-[#D4AF37]' : 'text-white/80'}`}>{a.label}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <FieldLabel hint="Pick all that apply">Deal Type / Strategy</FieldLabel>
                <MultiSelectChips
                  options={STRATEGIES}
                  value={form.strategies}
                  onChange={v => f('strategies', v)}
                />
                {form.strategies.includes('Other') && (
                  <div className="mt-3">
                    <TextInput value={form.other_strategy} onChange={v => f('other_strategy', v)} placeholder="Tell us your strategy…" data-testid="bespoke-other-strategy" />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <FieldLabel>Investment Budget</FieldLabel>
                  <SelectInput value={form.investment_budget} onChange={v => f('investment_budget', v)} options={BUDGETS} data-testid="bespoke-budget" />
                </div>
                <div>
                  <FieldLabel hint="Annual yield expectation">Target Yield / ROI</FieldLabel>
                  <TextInput value={form.target_yield} onChange={v => f('target_yield', v)} placeholder="e.g. 8–10% gross" data-testid="bespoke-yield" />
                </div>
                <div>
                  <FieldLabel hint="Over hold period">Capital Growth Objective</FieldLabel>
                  <TextInput value={form.capital_growth} onChange={v => f('capital_growth', v)} placeholder="e.g. 25–40% over 5 years" data-testid="bespoke-growth" />
                </div>
                <div>
                  <FieldLabel>Preferred Holding Period</FieldLabel>
                  <SelectInput value={form.holding_period} onChange={v => f('holding_period', v)} options={HOLDING_PERIODS} data-testid="bespoke-hold" />
                </div>
                <div className="md:col-span-2">
                  <FieldLabel>Timeline to Invest</FieldLabel>
                  <RadioCards options={TIMELINES} value={form.timeline} onChange={v => f('timeline', v)} columns={5} />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="bespoke-step-3">
              <SectionHeading icon={Briefcase} title="Financing & Investor Profile" subtitle="Helps us match the right opportunities and structures." />

              <div>
                <FieldLabel hint="Pick all that apply">Funding Position</FieldLabel>
                <MultiSelectChips options={FUNDING} value={form.funding_position} onChange={v => f('funding_position', v)} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <FieldLabel>Deposit / Available Capital</FieldLabel>
                  <TextInput value={form.deposit_available} onChange={v => f('deposit_available', v)} placeholder="e.g. £250,000" data-testid="bespoke-deposit" />
                </div>
                <div>
                  <FieldLabel>Experience Level</FieldLabel>
                  <SelectInput value={form.experience_level} onChange={v => f('experience_level', v)} options={EXPERIENCE} data-testid="bespoke-experience" />
                </div>
              </div>

              <div>
                <FieldLabel>Risk Appetite</FieldLabel>
                <RadioCards options={RISK} value={form.risk_appetite} onChange={v => f('risk_appetite', v)} columns={4} />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6" data-testid="bespoke-step-4">
              <SectionHeading icon={Sparkles} title="Additional Preferences" subtitle="Optional — but the more you share, the sharper the match." />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <FieldLabel>Unit Size / Bedroom Preference</FieldLabel>
                  <TextInput value={form.unit_preference} onChange={v => f('unit_preference', v)} placeholder="e.g. 2–3 bed, min 800 sq ft" data-testid="bespoke-unit" />
                </div>
                <div>
                  <FieldLabel>Priority</FieldLabel>
                  <RadioCards
                    options={[{ value: 'rental', label: 'Rental Income' }, { value: 'capital', label: 'Capital Growth' }, { value: 'balanced', label: 'Balanced' }]}
                    value={form.growth_vs_rental}
                    onChange={v => f('growth_vs_rental', v)}
                    columns={3}
                  />
                </div>
              </div>

              <div>
                <FieldLabel>Required Amenities / Features</FieldLabel>
                <TextInput value={form.amenities} onChange={v => f('amenities', v)} placeholder="e.g. parking, gym, concierge, freehold" data-testid="bespoke-amenities" />
              </div>

              <div>
                <FieldLabel>Specific Requirements / Exclusions</FieldLabel>
                <TextArea value={form.requirements_exclusions} onChange={v => f('requirements_exclusions', v)} placeholder="What's a 'yes', what's a 'no'?" rows={3} data-testid="bespoke-requirements" />
              </div>

              <div>
                <FieldLabel>Additional Notes / Bespoke Brief</FieldLabel>
                <TextArea value={form.additional_notes} onChange={v => f('additional_notes', v)} placeholder="Anything else our desk should know about your strategy, structure or constraints…" rows={4} data-testid="bespoke-notes" />
              </div>

              {/* Stay-in-the-loop channel preferences */}
              <div className="pt-5 border-t border-white/10">
                <p className="text-white/65 text-sm font-medium mb-1">Stay informed throughout your search</p>
                <p className="text-white/40 text-[12px] mb-3.5">Pick how you'd like to receive real-time updates from our team. You can change this anytime in your private portal.</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  {[
                    { key: 'notify_email',    label: 'Email',    desc: 'Detailed updates' },
                    { key: 'notify_whatsapp', label: 'WhatsApp', desc: 'Short push messages' },
                    { key: 'notify_telegram', label: 'Telegram', desc: 'One-tap real-time' },
                  ].map(c => (
                    <label
                      key={c.key}
                      className={`relative flex items-start gap-2.5 p-3.5 rounded-lg border cursor-pointer transition-all ${
                        form[c.key]
                          ? 'bg-[#D4AF37]/[0.05] border-[#D4AF37]/40'
                          : 'bg-white/[0.02] border-white/10 hover:border-white/25'
                      }`}
                      data-testid={`bespoke-${c.key}`}
                    >
                      <input
                        type="checkbox"
                        checked={!!form[c.key]}
                        onChange={e => f(c.key, e.target.checked)}
                        className="mt-0.5 accent-[#D4AF37] w-4 h-4 flex-shrink-0"
                      />
                      <div>
                        <p className={`text-sm font-semibold ${form[c.key] ? 'text-[#D4AF37]' : 'text-white/80'}`}>{c.label}</p>
                        <p className="text-white/40 text-[11px] mt-0.5">{c.desc}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Consent */}
              <div className="pt-4 border-t border-white/10 space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.consent} onChange={e => f('consent', e.target.checked)} className="mt-1 accent-[#D4AF37] w-4 h-4" data-testid="bespoke-consent" />
                  <span className="text-white/70 text-xs leading-relaxed">
                    I consent to Black Star PI processing my data to source matching opportunities. Every detail is held in strict confidence. <span className="text-[#D4AF37]">Required</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.mailing_list} onChange={e => f('mailing_list', e.target.checked)} className="mt-1 accent-[#D4AF37] w-4 h-4" data-testid="bespoke-mailing" />
                  <span className="text-white/70 text-xs leading-relaxed">
                    Subscribe me to the private Deal Alert mailing list for off-market opportunities.
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="pt-6 border-t border-white/10 flex items-center justify-between gap-3">
            <button
              onClick={() => step > 1 && setStep(step - 1)}
              disabled={step === 1}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors disabled:opacity-30 disabled:pointer-events-none"
              data-testid="bespoke-back-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <p className="text-[10px] uppercase tracking-[0.18em] text-white/35">
              Step <span className="text-[#D4AF37]">{step}</span> / 4
            </p>
            <button
              onClick={next}
              disabled={submitting}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B8941E] text-[#0a1a14] text-xs font-bold uppercase tracking-[0.18em] hover:shadow-lg hover:shadow-[#D4AF37]/25 transition-all disabled:opacity-50 disabled:pointer-events-none"
              data-testid="bespoke-next-btn"
            >
              {submitting ? 'Submitting…' : (step === 4 ? 'Submit Brief' : 'Continue')}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <p className="text-center text-[10px] text-white/30 uppercase tracking-[0.3em] mt-6">
          Black Star PI · Bespoke Sourcing Desk
        </p>
      </div>
    </div>
  );
};

const SectionHeading = ({ icon: Icon, title, subtitle }) => (
  <div className="flex items-start gap-3 pb-3 border-b border-white/5">
    <span className="inline-flex w-9 h-9 rounded-lg bg-[#D4AF37]/12 text-[#D4AF37] items-center justify-center flex-shrink-0 mt-0.5">
      <Icon className="w-4 h-4" />
    </span>
    <div>
      <h2 className="text-white text-lg font-semibold">{title}</h2>
      {subtitle && <p className="text-white/50 text-xs mt-0.5">{subtitle}</p>}
    </div>
  </div>
);

export default BespokeSourcingPage;
